import React, { Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Card, CardBody, CardHeader, Col, Container, Row, Label, Input, Spinner } from 'reactstrap';
import BreadCrumb from '../components/Common/BreadCrumb';

import { createDeepClone } from "../utils/index";

import CardLoader from "../components/CardLoader";
import GoogleMapModal from "../components/GoogleMapModal";
import ActionList from "../components/ActionList";

import { useDispatch, useSelector } from "react-redux";
import * as idiCustomerActions from "../actions/IdiCustomerActions";

const IdiCustomerEditScreen  = (props) => {
    const { register, clearErrors, reset, trigger, formState: { errors } } = useForm({mode: 'all'});

    const navigate = useNavigate();
    const { id, type } = useParams();

    const dispatch = useDispatch();

    const loading = useSelector((state) => state.idicustomer.loadingFBO);
    const fbo = useSelector((state) => state.idicustomer.fbo);
    
    const [fboState, setFBOState] = React.useState(fbo);
    const [isDirty, setIsDirty] = React.useState(false);
    const [refreshAction, setRefreshAction] = React.useState(false);
    const [showGoogleMap, setShowGoogleMap] = React.useState(false);

    React.useEffect(() => {
	    dispatch(idiCustomerActions.loadIdiCustomer(id, type, navigate));
    }, [id, type]);

    React.useEffect(() => {
		setFBOState(fbo);
        setIsDirty(false);
        setRefreshAction(!refreshAction);
        
        if(fbo){
            reset(fbo.customer);
        }

        clearErrors();
    }, [fbo]);

    const reloadAction = () => {
        setRefreshAction(!refreshAction);
    }

    const onIdiCustomerChange = (name, value) => {
        var copy_fboState = createDeepClone(fboState);
        if(value) {
            if(name === "latitude" || name === "longitude") {
                value = value.replace(/[^0-9.-]/g, '').replace(/(?!^)-/g, '').replace(/(\..*)\./g, '$1');
            }

            copy_fboState.customer[name] = value;
        }
        else if(value === false) {
            copy_fboState.customer[name] = false;
        }
        else {
            copy_fboState.customer[name] = null;
        }
        
        setFBOState(copy_fboState);
        setIsDirty(true);
    }

    const onAddressSelect = (locationData) => {
        if(locationData) {
            var copy_fboState = createDeepClone(fboState);

            copy_fboState.customer["address"] = locationData.address;
            copy_fboState.customer["country"] = locationData.country;
            copy_fboState.customer["city"] = locationData.city;
            copy_fboState.customer["state"] = locationData.state;
            copy_fboState.customer["zipCode"] = locationData.zipCode;
            copy_fboState.customer["latitude"] = locationData.latitude;
            copy_fboState.customer["longitude"] = locationData.longitude;

            setFBOState(copy_fboState);
            setIsDirty(true);
            setShowGoogleMap(false);
        }
    }

    const onSaveIdiCustomer = async() => {
        const valid = await trigger();
        if(valid) {
            dispatch(idiCustomerActions.saveIdiCustomer(fboState, navigate));
        }
    }

    const onCancelIdiCustomer = () => {
        setFBOState(fbo);
        setIsDirty(false);
        clearErrors();
    }
    
    return(
        <Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={fboState && fboState.customer.id > 0 ? `Edit ${fboState && fboState.title}` : `Add ${fboState && fboState.title}`} />
                    <Row>
                        <Col lg={fboState && fboState.customer.id > 0 ? 9 : 12}>
                            <CardLoader loading={loading} />

                            <Card>
                                <CardHeader className="border-0 d-flex align-items-center">
                                    <h5 className="card-title mb-0 flex-grow-1">{fboState && fboState.title}</h5>
                                    <div className="flex-shrink-0">
                                        <div className="d-flex flex-wrap gap-2">
                                            <button className="btn btn-primary btn-label"
                                                disabled={loading || !isDirty}
                                                onClick={onSaveIdiCustomer}
                                            >
                                                <i className="ri-save-line label-icon align-middle"></i> Save {fboState && fboState.title}
                                            </button>
                                            <button className="btn btn-danger btn-label"
                                                disabled={loading || !isDirty}
                                                onClick={onCancelIdiCustomer}
                                            >
                                                <i className="ri-close-line label-icon align-middle"></i> Cancel
                                            </button>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardBody className="border border-dashed border-end-0 border-start-0">
                                    <Row className="gy-2">
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Code</Label>
                                            <input type="text" maxLength={256} className="form-control"
                                                value={(fboState && fboState.customer.code) || ""}
                                                onChange={e => onIdiCustomerChange("code", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">First Name<span className="required">*</span></Label>
                                            <input type="text" maxLength={256} className={`form-control ${errors.firstName ? "is-invalid" : ""}`}
                                                title={errors.firstName?.message}
                                                value={(fboState && fboState.customer.firstName) || ""}
                                                {...register("firstName", { required: "First Name is required" })}
                                                onChange={e => onIdiCustomerChange("firstName", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Last Name<span className="required">*</span></Label>
                                            <input type="text" maxLength={256} className={`form-control ${errors.lastName ? "is-invalid" : ""}`}
                                                title={errors.lastName?.message}
                                                value={(fboState && fboState.customer.lastName) || ""}
                                                {...register("lastName", { required: "Last Name is required" })}
                                                onChange={e => onIdiCustomerChange("lastName", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Telephone Number<span className="required">*</span></Label>
                                            <input type="text" maxLength={256} className={`form-control ${errors.telephoneNumber ? "is-invalid" : ""}`}
                                                title={errors.telephoneNumber?.message}
                                                value={(fboState && fboState.customer.telephoneNumber) || ""}
                                                {...register("telephoneNumber", { required: "Telephone Number is required" })}
                                                onChange={e => onIdiCustomerChange("telephoneNumber", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Mobile Number</Label>
                                            <input type="text" maxLength={256} className="form-control"
                                                value={(fboState && fboState.customer.mobileNumber) || ""}
                                                onChange={e => onIdiCustomerChange("mobileNumber", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Fax Number</Label>
                                            <input type="text" maxLength={256} className="form-control"
                                                value={(fboState && fboState.customer.faxNumber) || ""}
                                                onChange={e => onIdiCustomerChange("faxNumber", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Email<span className="required">*</span></Label>
                                            <input type="text" maxLength={256} className={`form-control ${errors.email ? "is-invalid" : ""}`}
                                                title={errors.email?.message}
                                                value={(fboState && fboState.customer.email) || ""}
                                                {...register("email", { required: "Email is required" })}
                                                onChange={e => onIdiCustomerChange("email", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={12} md={12}>
                                            <Label className="form-label">Address<span className="required">*</span></Label>
                                            <div className="d-flex gap-2">
                                                <input type="text" maxLength={256} className={`form-control ${errors.address ? "is-invalid" : ""}`}
                                                    title={errors.address?.message}
                                                    value={(fboState && fboState.customer.address) || ""}
                                                    {...register("address", { required: "Address is required" })}
                                                    onChange={e => onIdiCustomerChange("address", e.target.value)}
                                                />
                                                {fboState &&
                                                <button className="btn btn-primary btn-icon float-end"
                                                    onClick={() => setShowGoogleMap(true)}
                                                >
                                                    <i className="ri-map-pin-line"></i>
                                                </button>
                                                }
                                            </div>
                                            
                                        </Col>
                                        <Col xxl={3} md={6}>
                                            <Label className="form-label">Country<span className="required">*</span></Label>
                                            <input type="text" maxLength={256} className={`form-control ${errors.country ? "is-invalid" : ""}`}
                                                title={errors.country?.message}
                                                value={(fboState && fboState.customer.country) || ""}
                                                {...register("country", { required: "Country is required" })}
                                                onChange={e => onIdiCustomerChange("country", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={3} md={6}>
                                            <Label className="form-label">City<span className="required">*</span></Label>
                                            <input type="text" maxLength={256} className={`form-control ${errors.city ? "is-invalid" : ""}`}
                                                title={errors.city?.message}
                                                value={(fboState && fboState.customer.city) || ""}
                                                {...register("city", { required: "City is required" })}
                                                onChange={e => onIdiCustomerChange("city", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={3} md={6}>
                                            <Label className="form-label">State</Label>
                                            <input type="text" maxLength={256} className="form-control"
                                                value={(fboState && fboState.customer.state) || ""}
                                                onChange={e => onIdiCustomerChange("state", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={3} md={6}>
                                            <Label className="form-label">Zip Code</Label>
                                            <input type="text" maxLength={256} className="form-control"
                                                value={(fboState && fboState.customer.zipCode) || ""}
                                                onChange={e => onIdiCustomerChange("zipCode", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={3} md={6}>
                                            <Label className="form-label">Latitude</Label>
                                            <input type="text" className="form-control"
                                                value={(fboState && fboState.customer.latitude) || ""}
                                                onChange={e => onIdiCustomerChange("latitude", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={3} md={6}>
                                            <Label className="form-label">Longitude</Label>
                                            <input type="text" className="form-control"
                                                value={(fboState && fboState.customer.longitude) || ""}
                                                onChange={e => onIdiCustomerChange("longitude", e.target.value)}
                                            />
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                        
                        {fboState && fboState.customer.id > 0 &&
                        <Col lg={3}>
                            <ActionList id={fboState.customer.id} name={"IDI_CUSTOMER"} refreshAction={refreshAction} />
                        </Col>
                        }
                    </Row>
                </Container>
            </div>

            {fboState && showGoogleMap &&
            <GoogleMapModal
                show={showGoogleMap}
                onYesClick={onAddressSelect}
                onCloseClick={() => setShowGoogleMap(false)} 
                address={fboState.customer.address || ""}
                latitude={fboState.customer.latitude || null}
                longitude={fboState.customer.longitude || null}
            />
            }
        </Fragment>
    );
}

export default IdiCustomerEditScreen;